/**
 * IZAS Custom Search System - VERSION FINAL (Dinámica + Scroll Infinito)
 * Sin tecnologías, con detalles técnicos mejorados y precio dinámico
 * + Diccionario dinámico de nombres de producto para el corrector de typos
 * + Banner debajo del carrusel de colecciones
 * + Scroll infinito: sigue paginando /search hasta agotar resultados
 */
class IzasSearch {
  constructor() {
    this.searchInput = null;
    this.searchResults = null;
    this.searchFilters = null;
    this.searchOverlay = null;
    this.searchWrapper = null;
    this.isOpen = false;
    this.currentQuery = '';
    this.debounceTimer = null;
    this.allFetchedProducts = []; 
    this.allFetchedProductIds = new Set(); // Deduplica rápido entre páginas y términos
    this.searchPagination = {};            // { termino: { page, hasMore } }
    this.isLoadingMorePage = false;
    this.searchRequestId = 0;              // Descarta respuestas de una búsqueda vieja
    this.scrollObserver = null;            // IntersectionObserver del infinite scroll
    
    this.savedCollections = [];
    this.savedCorrectedQuery = null;
    // Diccionario dinámico de nombres de producto (ej: "redoubt", "frosty")
    this.productKeywords = [];
    
    this.activeFilters = {
      gender: [],
      color: [],
      priceRange: null,
      technicalDetails: []
    };
    // --- DICCIONARIOS DE BÚSQUEDA ---
    this.synonyms = {
      'capucha': ['gorro'],
      'chaqueta': ['chamarra', 'zamarra', 'abrigo', 'anorak', 'chaquetón', 'cazadora', 'tercera capa', 'campera'],
      'tubular': ['cuello', 'braga', 'calentador de cuello', 'bufanda'],
      'zapatillas': ['deportivas', 'bambas', 'tenis', 'playeras', 'zapato', 'maripí', 'maripís', 'sneakers'],
      'mochila': ['bolsa', 'backpack', 'morral'],
      'pala': ['raqueta'],
      'chaqueta de fibra': ['chaqueta de pluma', 'plumas', 'plumífero', 'plumíferos', 'bomber', 'chaqueta rellena'],
      'frosty': ['escarpines', 'cangrejeras'],
      'chaqueta impermeable': ['chubasquero', 'poncho', 'waterproof', 'resistente al agua'],
      'polo': ['niqui'],
      'botas de trekking': ['botas de monte'],
      'calzado': ['botas', 'chanclas', 'pantuflas', 'sandalias'],
      'trekking': ['senderismo', 'hiking', 'montaña'],
      'bastones': ['palos'],
      'pantalón trekking': ['pantalón de montaña', 'pantalón', 'pantalones', 'jeans', 'mallas'],
      'zapatillas de descanso': ['zuecos', 'zapatillas casa', 'alpargatas'],
      'mallas': ['leggins', 'leggings', 'legins', 'legings'],
      'polar': ['forro', 'fleece', 'forro polar'],
      'parka': ['chaqueta larga', 'sobretodo'],
      'mount-tex': ['goretex'],
      'chaqueta de punto': ['rebeca'],
      'tienda': ['carpa', 'tent', 'tienda de campaña'],
      'saco': ['sleeping bag', 'saco de dormir'],
      'térmica': ['termica', 'thermal', 'caliente'],
      'sudadera': ['hoodie', 'buzo', 'jersey'],
      'gorra': ['cap', 'visera', 'sombrero'],
      'guantes': ['gloves', 'manoplas'],
      'calcetines': ['calcetin', 'medias', 'socks'],
      'cortavientos': ['windbreaker', 'rompe vientos']
    };
    // --- CONCEPTOS DE COLOR ---
    this.COLOR_CONCEPTS = {
      AMARILLO: { canonical: "amarillo", matches: ["gold honey", "illuminating", "illuminating/fair aqua", "illuminating/navy blue", "light yellow", "light yellow/blue", "light yellow/fair aqua", "light yellow/illuminating", "spring", "spring/royal", "spring/spring", "yellow", "yellow fluor", "yellow fluor/black"] },
      AZUL: { canonical: "azul", matches: ["blue", "blue river/bluemoon", "blue/orange", "celestial", "celestial/illuminating", "ceramic", "ceramic/atlantic", "ceramic/ceramic", "ceramic/ceramic/fucsia", "ceramic/dark purple", "ceramic/fucsia fluor", "coronet blue", "coronet blue/light blue", "fair aqua", "fair aqua/diva pink", "fair aqua/light yellow", "fair aqua/yellow", "grey blue/diva pink", "ink blue", "light blue", "light blue/blue", "light blue/coral fluor", "light blue/coronet blue", "light blue/navy blue", "mineral blue", "mineral blue/light blue", "sky blue", "turquoise", "turquoise/charcoal", "turquoise/smoke", "turquoise/turquoise", "atlantic", "atlantic/ceramic", "atlantic/lima", "atlantic/opal/lima", "blue indigo/diva pink", "bluemoon", "bluemoon/coral fluor", "bluemoon/dark blue", "bluemoon/silver", "dark aqua", "dark aqua/red", "dark blue", "grey blue/orange", "jeans", "light blue/ice/navy blue", "navy blue", "navy blue degraded", "navy blue/aqua", "navy blue/blue", "navy blue/celestial/illuminati", "navy blue/coral fluor", "navy blue/fair aqua", "navy blue/gold honey", "navy blue/honey", "navy blue/illuminating", "navy blue/light blue", "navy blue/light blue/coral", "navy blue/light blue/diva pink", "navy blue/rose", "navy blue/fair aqua", "navy/spring", "royal", "royal/coral fluor", "royal/royal", "royal/spring", "royal/white/srping"] },
      BEIGE: { canonical: "beige", matches: ["alfalfa/hielo", "aluminio", "aluminium/tapioca", "aliminium", "beige", "beige/rose", "stone", "tapioca", "tapioca/brown", "toasted", "tostado", "tostado/alfalfa"] },
      BLANCO: { canonical: "blanco", matches: ["hielo/alfalfa", "white", "white/atlantic", "white/black", "white/blue", "white/diva pink", "white/fair aqua", "white/gold", "white/lilac", "white/menta", "white/menta", "white/multicolor", "white/multicolour", "white/navy blue", "white/purple", "white/silver", "white/smoke/black", "white/white", "white/yellow fluor"] },
      GRIS: { canonical: "gris", matches: ["aluminio metal", "aluminum/tapioca", "charcoal", "charcoal/lotus", "charcoal/orange", "dark grey", "dark grey/black", "dark grey/orange", "grey", "grey lilac", "ice", "silver", "silver/black", "silver/diva pink", "smoke", "smoke/black", "smoke/black/red", "smoke/blue river", "smoke/ceramic", "smoke/diva pink", "smoke/lotus", "smoke/orange", "smoke/silver/lotus", "smoke/turquoise"] },
      MARRON: { canonical: "marron", matches: ["almendra", "brown", "brown/dark brown", "chocolate", "natural", "natural/chocolate"] },
      MORADO: { canonical: "morado", matches: ["dark purple", "grey lilac/diva pink", "lavanda", "lilac", "lilac grey", "lilac/diva pink", "lilac/purple", "lilac/violet", "orquidea", "purple", "violet", "violet/diva pink", "violet/grey lilac", "violet/lilac", "wine"] },
      MULTICOLOR: { canonical: "multicolor", matches: ["autumnal", "dark atlantic/fair aqua/diva pink", "logo", "multicolor", "multicolor 2", "multicolor 3", "pilar", "virgen"] },
      NARANJA: { canonical: "naranja", matches: ["apricot", "cheddar", "coral", "coral fluor", "coral fluor/navy blue", "coral/jade", "coral/wine", "light orange", "peach"] },
      NEGRO: { canonical: "negro", matches: ["black", "black/black", "black/dark grey", "black/dark grey/orange", "black/dark grey/royal", "black/diva pink", "black/fucsia", "black/grey", "black/light green", "black/lilac", "black/orange", "black/red", "black/royal", "black/silver", "black/smoke", "black/white", "black/yellow fluor"] },
      ROJO: { canonical: "rojo", matches: ["barberry", "barberry/diva pink", "beet red/diva pink", "cherry", "magenta", "mineral red", "mineral red/diva pink", "red", "red/black", "red/red", "red/smoke"] },
      ROSA: { canonical: "rosa", matches: ["crystal pink", "crystal pink/rose", "diva pink", "diva pink/beet red", "diva pink/magenta", "diva pink/mineral red", "diva pink/smoke", "fucsia", "fucsia fluor", "fucsia fluor/yellow fluor", "fucsia/black", "fucsia/fucsia", "light rose", "lotus", "lotus/silver", "magenta/diva pink", "pink", "pink fluor", "pink/silver", "raspberry", "rose", "rose/dark rose", "rose/ice/brown", "rose/light rose"] },
      VERDE: { canonical: "verde", matches: ["alfalfa", "alfalfa/lime", "atlantic degraded", "caki", "caki/dark caki", "dark atlantic", "dark atlantic/diva pink", "fair aqua", "fair aqua/dark atlantic", "fair aqua/dark atlantic/diva pink", "fair aqua/fucsia", "green", "green/atlantic", "green/fucsia", "jade", "light caki", "light caki/peach", "light green", "light lime", "light matcha/matcha", "light menta", "light menta/mineral blue", "lima", "lima/atlantic", "lime", "lime/royal", "matcha", "menta", "menta/atlantic", "menta/light yellow", "menta/wasabi", "menta/yellow", "mineral blue/light menta", "oil", "opal", "opal/atlantic", "pino/naranja", "wasabi"] }
    };
    // Diccionario visual de colores (Hexadecimales para las pelotas)
    this.colorHexMap = {
      "amarillo": "#eab308", "azul": "#3b82f6", "beige": "#d4d4d8",
      "blanco": "#ffffff", "gris": "#9ca3af", "marron": "#78350f",
      "morado": "#a855f7", "multicolor": "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
      "naranja": "#f97316", "negro": "#111827", "rojo": "#ef4444",
      "rosa": "#ec4899", "verde": "#22c55e"
    };
    this.init();
  }
  init() {
    this.disableNativeSearch();
    this.createSearchUI();
    this.attachEventListeners();
    this.renderFilters();
    this.loadProductKeywords(); // Carga/cachea nombres de producto para el corrector de typos
    // Si el usuario tocó el icono de búsqueda antes de que termináramos de
    // inicializar, el listener temprano (inline en el liquid) dejó esta
    // marca. La consumimos aquí y abrimos el buscador directamente.
    if (window.__izasPendingOpen) {
      window.__izasPendingOpen = false;
      this.open();
    }
  }
  // ============================================================
  // BLOQUE: Diccionario dinámico de nombres de producto
  // ============================================================
  /**
   * Quita acentos y caracteres raros para comparar de forma robusta.
   */
  normalizeForKeywords(str) {
    return str
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .trim();
  }
  /**
   * Extrae palabras "candidatas a nombre de modelo" a partir de una lista
   * de títulos de producto. Filtra palabras genéricas (categorías, género,
   * tallas, conectores) para quedarnos solo con nombres propios tipo
   * "Redoubt", "Frosty", "Aurora", etc.
   */
  buildProductKeywordsFromTitles(titles) {
    const stopWords = new Set([
      // conectores / artículos
      'de', 'la', 'el', 'los', 'las', 'con', 'para', 'y', 'en', 'del', 'al',
      // categorías habituales (amplía según tu catálogo)
      'calcetin', 'calcetines', 'chaqueta', 'chaquetas', 'pantalon', 'pantalones',
      'mochila', 'mochilas', 'camiseta', 'camisetas', 'sudadera', 'sudaderas',
      'zapatilla', 'zapatillas', 'bota', 'botas', 'guante', 'guantes',
      'gorro', 'gorra', 'gorras', 'bufanda', 'bufandas', 'mallas', 'malla',
      'polar', 'polares', 'parka', 'parkas', 'forro', 'chubasquero',
      'cortavientos', 'tienda', 'saco', 'sacos', 'bastones', 'palas', 'pala',
      'raqueta', 'raquetas',
      // genero / publico
      'hombre', 'mujer', 'unisex', 'niño', 'niña', 'nino', 'nina', 'kids', 'junior',
      // descriptores comunes que no aportan como "nombre"
      'montana', 'trekking', 'running', 'impermeable', 'transpirable',
      'termica', 'termico', 'ligero', 'ligera', 'manga', 'corta', 'larga',
      'set', 'pack', 'conjunto'
    ]);
    const keywords = new Set();
    titles.forEach(title => {
      if (!title || typeof title !== 'string') return;
      const words = this.normalizeForKeywords(title).split(/\s+/);
      words.forEach(w => {
        // descarta tallas tipo "v3", "xl", números sueltos, palabras cortas y stopwords
        if (
          w.length >= 4 &&
          !stopWords.has(w) &&
          !/^v\d+$/.test(w) &&
          !/^\d+$/.test(w)
        ) {
          keywords.add(w);
        }
      });
    });
    return [...keywords];
  }
  /**
   * Carga el diccionario de nombres de producto desde /search-keywords,
   * paginando si hace falta, y lo cachea en localStorage 24h para no
   * golpear el servidor en cada visita.
   */
  async loadProductKeywords() {
    const CACHE_KEY = 'izas_product_keywords';
    const CACHE_TTL = 1000 * 60 * 60 * 24; // 24 horas
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Array.isArray(cached.keywords) && (Date.now() - cached.timestamp < CACHE_TTL)) {
        this.productKeywords = cached.keywords;
        return;
      }
    } catch (e) {
      // cache corrupta o localStorage no disponible, seguimos sin problema
    }
    try {
      let allTitles = [];
      let page = 1;
      let hasNextPage = true;
      const MAX_PAGES = 20; // límite de seguridad (20 x 250 = 5000 productos)
      while (hasNextPage && page <= MAX_PAGES) {
        const data = await fetch(`/pages/search-keywords?page=${page}&view=ajax`).then(r => r.json());
        if (Array.isArray(data.titles)) {
          allTitles = allTitles.concat(data.titles);
        }
        hasNextPage = !!data.has_next_page;
        page++;
      }
      this.productKeywords = this.buildProductKeywordsFromTitles(allTitles);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          timestamp: Date.now(),
          keywords: this.productKeywords
        }));
      } catch (e) {
        // si localStorage está lleno o bloqueado, no pasa nada, simplemente no cacheamos
      }
    } catch (error) {
      console.error('IzasSearch: no se pudieron cargar las keywords de producto', error);
      this.productKeywords = this.productKeywords || [];
    }
  }
  // ============================================================
  // FIN BLOQUE
  // ============================================================
  levenshteinDistance(a, b) {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    const matrix = [];
    for (let i = 0; i <= b.length; i++) { matrix[i] = [i]; }
    for (let j = 0; j <= a.length; j++) { matrix[0][j] = j; }
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[b.length][a.length];
  }
  correctTypos(query) {
    const queryLower = query.toLowerCase().trim();
    if (!queryLower) return '';
    let fullDictionary = Object.keys(this.synonyms);
    Object.values(this.synonyms).forEach(synList => fullDictionary = fullDictionary.concat(synList));
    // Añadimos los nombres de producto aprendidos dinámicamente
    if (this.productKeywords && this.productKeywords.length > 0) {
      fullDictionary = fullDictionary.concat(this.productKeywords);
    }
    fullDictionary = [...new Set(fullDictionary)];
    if (fullDictionary.includes(queryLower)) {
      return queryLower;
    }
    let bestPhraseMatch = queryLower;
    let minPhraseDistance = 99;
    let phraseThreshold = queryLower.length >= 10 ? 2 : 1;
    fullDictionary.forEach(validPhrase => {
      if (Math.abs(queryLower.length - validPhrase.length) <= phraseThreshold) {
        const distance = this.levenshteinDistance(queryLower, validPhrase);
        if (distance <= phraseThreshold && distance < minPhraseDistance) {
          minPhraseDistance = distance;
          bestPhraseMatch = validPhrase;
        }
      }
    });
    if (minPhraseDistance <= phraseThreshold) {
      return bestPhraseMatch;
    }
    let validSingleWords = [];
    fullDictionary.forEach(phrase => {
      validSingleWords = validSingleWords.concat(phrase.split(' '));
    });
    validSingleWords = [...new Set(validSingleWords)];
    const words = queryLower.split(' ');
    const correctedWords = words.map(word => {
      if (word.length <= 3) return word;
      if (validSingleWords.includes(word)) return word;
      let bestWordMatch = word;
      let minWordDistance = 99;
      let wordThreshold = word.length >= 7 ? 2 : 1;
      validSingleWords.forEach(validWord => {
        if (Math.abs(word.length - validWord.length) <= wordThreshold) {
          const distance = this.levenshteinDistance(word, validWord);
          if (distance <= wordThreshold && distance < minWordDistance) {
            minWordDistance = distance;
            bestWordMatch = validWord;
          }
        }
      });
      return bestWordMatch;
    });
    return correctedWords.join(' ');
  }
  expandWithSynonyms(query) {
    const queryLower = query.toLowerCase().trim();
    let expandedTerms = [queryLower];
    Object.entries(this.synonyms).forEach(([key, synonymList]) => {
      if (key === queryLower) expandedTerms = expandedTerms.concat(synonymList);
      else if (synonymList.includes(queryLower)) expandedTerms.push(key);
    });
    const words = queryLower.split(' ');
    words.forEach(word => {
      if (word.length <= 3) return;
      if (this.synonyms[word]) expandedTerms = expandedTerms.concat(this.synonyms[word]);
      Object.entries(this.synonyms).forEach(([key, synonymList]) => {
        if (synonymList.includes(word)) expandedTerms.push(key);
      });
    });
    return [...new Set(expandedTerms)];
  }
  /**
   * Normaliza texto para comparaciones de relevancia: minúsculas y sin acentos.
   */
  normalizeForMatch(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
  /**
   * Filtro de relevancia: nos quedamos solo con los productos cuyo título
   * (o handle) contiene de verdad el término buscado. Sin esto, en páginas
   * profundas Shopify empieza a devolver productos "relacionados" por tags
   * o colecciones compartidas (p. ej. sudaderas/chalecos/pantalones
   * etiquetados también como "capa intermedia" junto a las camisetas), y
   * el scroll infinito los va arrastrando todos.
   */
  filterByTermRelevance(products, term) {
    const normalizedTerm = this.normalizeForMatch(term).trim();
    if (!normalizedTerm) return products;
    // Comparamos palabra a palabra, no como frase contigua: "mallas madill"
    // debe encajar con "MALLAS CORTAS MADILL SHORT" (hay palabras por medio).
    const tokens = normalizedTerm.split(/\s+/).filter(Boolean);
    return products.filter(p => {
      const title = this.normalizeForMatch(p.title);
      const handle = this.normalizeForMatch((p.handle || '').replace(/-/g, ' '));
      const haystack = `${title} ${handle}`;
      return tokens.every(token => haystack.includes(token));
    });
  }
  createSearchUI() {
    this.searchOverlay = document.createElement('div');
    this.searchOverlay.className = 'izas-search-overlay';
    this.searchOverlay.style.display = 'none';
    
    const searchContainer = document.createElement('div');
    searchContainer.className = 'izas-search-container';
    
    searchContainer.innerHTML = `
      <div class="izas-search-wrapper">
        <div class="izas-search-header">
          <a href="/" class="izas-search-logo">
            <img src="https://www.izas-outdoor.com/cdn/shop/files/logo-izas-negro_2.svg?v=1707914877&width=100" alt="Izas Outdoor">
          </a>
          
          <div class="izas-search-input-wrapper">
            <svg class="izas-search-icon" viewBox="0 0 20 20" fill="none">
              <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <input type="text" class="izas-search-input" placeholder="Buscar productos, colecciones..." autocomplete="off" spellcheck="false" />
            <button class="izas-search-clear" style="display: none;">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M15 5L5 15M5 5l10 10"/></svg>
            </button>
          </div>
          <button class="izas-search-close">Cancelar</button>
        </div>
        
        <div class="izas-search-content">
          <button class="izas-mobile-filter-toggle" style="display: none;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
            <span>Mostrar Filtros</span>
          </button>
          
          <aside class="izas-search-filters" style="display: none;"></aside>
          <main class="izas-search-results"></main>
        </div>
      </div>
    `;
    this.searchOverlay.appendChild(searchContainer);
    document.body.appendChild(this.searchOverlay);
    this.searchInput = searchContainer.querySelector('.izas-search-input');
    this.searchResults = searchContainer.querySelector('.izas-search-results');
    this.searchFilters = searchContainer.querySelector('.izas-search-filters');
    this.clearButton = searchContainer.querySelector('.izas-search-clear');
    this.searchWrapper = searchContainer.querySelector('.izas-search-wrapper');
    this.mobileFilterToggle = searchContainer.querySelector('.izas-mobile-filter-toggle');
  }
  renderFilters() {
    let html = `
      <h3 class="izas-filter-title">Filtros</h3>
      
      <div class="izas-filter-group izas-collapsed">
        <div class="izas-filter-header">
          <h4 class="izas-filter-label">Ordenar por</h4>
          <svg class="izas-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="izas-filter-content">
          <div class="izas-radio-group">
            <label class="izas-radio-label">
              <input type="radio" name="izas-sort" value="relevance" checked>
              <span>Relevancia</span>
            </label>
            <label class="izas-radio-label">
              <input type="radio" name="izas-sort" value="newest">
              <span>Novedades</span>
            </label>
            <label class="izas-radio-label">
              <input type="radio" name="izas-sort" value="price-asc">
              <span>Precio: Menor a Mayor</span>
            </label>
            <label class="izas-radio-label">
              <input type="radio" name="izas-sort" value="price-desc">
              <span>Precio: Mayor a Menor</span>
            </label>
            <label class="izas-radio-label">
              <input type="radio" name="izas-sort" value="title-asc">
              <span>Nombre: A - Z</span>
            </label>
            <label class="izas-radio-label">
              <input type="radio" name="izas-sort" value="title-desc">
              <span>Nombre: Z - A</span>
            </label>
          </div>
        </div>
      </div>
      <div class="izas-filter-group">
        <div class="izas-filter-header">
          <h4 class="izas-filter-label">Género</h4>
          <svg class="izas-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="izas-filter-content">
          <div class="izas-filter-options" data-filter-type="gender">
            <button class="izas-filter-btn" data-value="hombre">Hombre</button>
            <button class="izas-filter-btn" data-value="mujer">Mujer</button>
            <button class="izas-filter-btn" data-value="unisex">Unisex</button>
          </div>
        </div>
      </div>
      <div class="izas-filter-group">
        <div class="izas-filter-header">
          <h4 class="izas-filter-label">Color</h4>
          <svg class="izas-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="izas-filter-content">
          <div class="izas-color-swatches" data-filter-type="color">
    `;
    for (const key in this.COLOR_CONCEPTS) {
      const canonical = this.COLOR_CONCEPTS[key].canonical;
      const bg = this.colorHexMap[canonical] || canonical;
      const bgStyle = bg.includes('gradient') ? `background: ${bg};` : `background-color: ${bg};`;
      html += `<button class="izas-color-swatch-btn" data-value="${canonical}" style="${bgStyle}" title="${canonical.charAt(0).toUpperCase() + canonical.slice(1)}"></button>`;
    }
    html += `
          </div>
        </div>
      </div>
      <div class="izas-filter-group">
        <div class="izas-filter-header">
          <h4 class="izas-filter-label">Precio</h4>
          <svg class="izas-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="izas-filter-content">
          <div class="izas-price-slider-wrapper">
            <div class="izas-price-slider-track"></div>
            <div class="izas-price-slider-range"></div>
            <input type="range" class="izas-price-slider izas-price-min" min="0" max="300" value="0" step="5">
            <input type="range" class="izas-price-slider izas-price-max" min="0" max="300" value="300" step="5">
            <div class="izas-price-values">
              <span class="izas-price-value-min">0€</span>
              <span class="izas-price-value-max">300€</span>
            </div>
          </div>
        </div>
      </div>
      <div class="izas-filter-group" id="izas-filter-group-details" style="display:none;">
        <div class="izas-filter-header">
          <h4 class="izas-filter-label">Detalles Técnicos</h4>
          <svg class="izas-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="izas-filter-content izas-checkbox-group" data-filter-type="technicalDetails" id="izas-dynamic-details"></div>
      </div>
      <button class="izas-filter-clear">Limpiar filtros</button>
    `;
    
    this.searchFilters.innerHTML = html;
    this.updateSliderTrack();
  }
  updateSliderTrack() {
    const minSlider = this.searchFilters.querySelector('.izas-price-min');
    const maxSlider = this.searchFilters.querySelector('.izas-price-max');
    const rangeTrack = this.searchFilters.querySelector('.izas-price-slider-range');
    
    if (!minSlider || !maxSlider || !rangeTrack) return;
    
    const min = parseInt(minSlider.value);
    const max = parseInt(maxSlider.value);
    const sliderMin = parseInt(minSlider.min);
    const sliderMax = parseInt(minSlider.max);
    
    const percentMin = ((min - sliderMin) / (sliderMax - sliderMin)) * 100;
    const percentMax = ((max - sliderMin) / (sliderMax - sliderMin)) * 100;
    
    rangeTrack.style.left = percentMin + '%';
    rangeTrack.style.width = (percentMax - percentMin) + '%';
  }
  // Calcula el precio máximo real y ajusta el slider
  initDynamicPrice(products) {
    if (!products || products.length === 0) return;
    
    let maxPrice = 0;
    products.forEach(p => {
      const price = parseFloat(p.price || p.variants?.[0]?.price || 0);
      if (price > maxPrice) maxPrice = price;
    });
    // Redondear hacia arriba a la decena más cercana (ej: 83 -> 90)
    maxPrice = Math.ceil(maxPrice / 10) * 10;
    if (maxPrice < 10) maxPrice = 10;
    const minSlider = this.searchFilters.querySelector('.izas-price-min');
    const maxSlider = this.searchFilters.querySelector('.izas-price-max');
    const maxDisplay = this.searchFilters.querySelector('.izas-price-value-max');
    if (minSlider && maxSlider && maxDisplay) {
      minSlider.max = maxPrice;
      maxSlider.max = maxPrice;
      
      minSlider.value = 0;
      maxSlider.value = maxPrice;
      
      this.searchFilters.querySelector('.izas-price-value-min').textContent = '0€';
      maxDisplay.textContent = maxPrice + '€';
      
      this.activeFilters.priceRange = { min: 0, max: maxPrice };
      this.updateSliderTrack();
    }
  }
  /**
   * Amplía el tope del slider de precio si llegan productos más caros por
   * el scroll infinito, SIN pisar un valor que el usuario ya haya elegido.
   */
  extendDynamicPriceRange(products) {
    if (!products || products.length === 0) return;

    let maxPrice = 0;
    products.forEach(p => {
      const price = parseFloat(p.price || p.variants?.[0]?.price || 0);
      if (price > maxPrice) maxPrice = price;
    });
    maxPrice = Math.ceil(maxPrice / 10) * 10;
    if (maxPrice < 10) maxPrice = 10;

    const minSlider = this.searchFilters.querySelector('.izas-price-min');
    const maxSlider = this.searchFilters.querySelector('.izas-price-max');
    const maxDisplay = this.searchFilters.querySelector('.izas-price-value-max');
    if (!minSlider || !maxSlider || !maxDisplay) return;

    const currentSliderMax = parseInt(maxSlider.max);
    if (maxPrice > currentSliderMax) {
      minSlider.max = maxPrice;
      maxSlider.max = maxPrice;
      // Solo movemos el thumb si el usuario seguía en el máximo por defecto
      // (si lo ha arrastrado a un valor propio, respetamos su elección)
      if (parseInt(maxSlider.value) === currentSliderMax) {
        maxSlider.value = maxPrice;
        maxDisplay.textContent = maxPrice + '€';
        if (this.activeFilters.priceRange) {
          this.activeFilters.priceRange.max = maxPrice;
        }
      }
      this.updateSliderTrack();
    }
  }
  populateDynamicFilters(products) {
    const detailsSet = new Set();
    // Extraemos valores únicos limpios
    products.forEach(p => {
      if (p.technical_details_meta) {
        const details = typeof p.technical_details_meta === 'string' 
          ? p.technical_details_meta.split(',') 
          : Array.isArray(p.technical_details_meta) ? p.technical_details_meta : [p.technical_details_meta];
        
        details.forEach(d => { 
          if (d && typeof d === 'string' && d !== '""') {
            const clean = d.trim();
            const formatted = clean.charAt(0).toUpperCase() + clean.slice(1);
            detailsSet.add(formatted);
          }
        });
      }
    });
    const detailsGroup = document.getElementById('izas-filter-group-details');
    const detailsContainer = document.getElementById('izas-dynamic-details');
    
    if (detailsGroup && detailsContainer) {
      if (detailsSet.size > 0) {
        detailsGroup.style.display = 'block';
        detailsContainer.innerHTML = Array.from(detailsSet).sort().map(detail => `
          <label class="izas-checkbox-label">
            <input type="checkbox" value="${detail}" ${this.activeFilters.technicalDetails.includes(detail) ? 'checked' : ''}>
            <span>${detail}</span>
          </label>
        `).join('');
      } else {
        detailsGroup.style.display = 'none';
      }
    }
  }
  attachEventListeners() {
    const searchTriggers = document.querySelectorAll('.js-show-search, .trigger-show-search, [aria-controls="search-bar"], input[name="q"], .search__input');
    searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    });
    this.searchOverlay.querySelector('.izas-search-close').addEventListener('click', () => this.close());
    this.searchOverlay.addEventListener('click', (e) => {
      if (e.target === this.searchOverlay) this.close();
    });
    this.searchInput.addEventListener('input', (e) => {
      this.handleInput(e.target.value);
    });
    this.clearButton.addEventListener('click', () => {
      this.searchInput.value = '';
      this.currentQuery = '';
      this.clearButton.style.display = 'none';
      this.searchFilters.style.display = 'none';
      this.showDefaultResults();
      this.searchInput.focus();
    });
    this.mobileFilterToggle.addEventListener('click', () => {
      this.searchFilters.classList.toggle('izas-filters-open');
      const span = this.mobileFilterToggle.querySelector('span');
      if (this.searchFilters.classList.contains('izas-filters-open')) {
        span.textContent = 'Ocultar Filtros';
      } else {
        span.textContent = 'Mostrar Filtros';
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (!this.isOpen) this.open();
      }
    });
    window.addEventListener('resize', () => {
      clearTimeout(this.bannerResizeTimer);
      this.bannerResizeTimer = setTimeout(() => {
        if (this.isOpen && !this.currentQuery) this.syncBannerWidth();
      }, 150);
    });
    this.searchFilters.addEventListener('click', (e) => {
      // 1. Acordeón
      const header = e.target.closest('.izas-filter-header');
      if (header) {
        header.closest('.izas-filter-group').classList.toggle('izas-collapsed');
        return;
      }
      // 2. Botones de género y pelotas
      if (e.target.classList.contains('izas-filter-btn') || e.target.classList.contains('izas-color-swatch-btn')) {
        e.target.classList.toggle('active');
        this.updateActiveFilters();
        this.applyFiltersAndDisplay();
      }
      
      // 3. Limpiar filtros
      if (e.target.classList.contains('izas-filter-clear')) {
        this.searchFilters.querySelectorAll('.izas-filter-btn, .izas-color-swatch-btn').forEach(btn => btn.classList.remove('active'));
        this.searchFilters.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false);
        
        // Al limpiar, calculamos el precio máximo otra vez desde cero
        this.initDynamicPrice(this.allFetchedProducts);
        
        // Resetear la ordenación a Relevancia
        const defaultSort = this.searchFilters.querySelector('input[name="izas-sort"][value="relevance"]');
        if (defaultSort) defaultSort.checked = true;
        this.activeFilters = { gender: [], color: [], priceRange: null, technicalDetails: [] };
        // Reactivamos el precio máximo al estado "virgen"
        this.updatePriceFilter(); 
      }
    });
    this.searchFilters.addEventListener('change', (e) => {
      // Si cambia la ordenación (los nuevos radios) o un checkbox de detalles
      if (e.target.name === 'izas-sort' || e.target.type === 'checkbox') {
        this.updateActiveFilters(); // Actualizamos las variables de detalles
        this.applyFiltersAndDisplay(); // Pintamos de nuevo los productos ordenados/filtrados
      }
    });
    this.searchFilters.addEventListener('input', (e) => {
      if (e.target.classList.contains('izas-price-min')) {
        const minValue = parseInt(e.target.value);
        const maxSlider = this.searchFilters.querySelector('.izas-price-max');
        const maxValue = parseInt(maxSlider.value);
        
        if (minValue > maxValue - 5) e.target.value = maxValue - 5;
        
        this.searchFilters.querySelector('.izas-price-value-min').textContent = e.target.value + '€';
        this.updateSliderTrack();
        this.updatePriceFilter();
      }
      
      if (e.target.classList.contains('izas-price-max')) {
        const maxValue = parseInt(e.target.value);
        const minSlider = this.searchFilters.querySelector('.izas-price-min');
        const minValue = parseInt(minSlider.value);
        
        if (maxValue < minValue + 5) e.target.value = minValue + 5;
        
        this.searchFilters.querySelector('.izas-price-value-max').textContent = e.target.value + '€';
        this.updateSliderTrack();
        this.updatePriceFilter();
      }
    });
  }
  updatePriceFilter() {
    const minSlider = this.searchFilters.querySelector('.izas-price-min');
    const maxSlider = this.searchFilters.querySelector('.izas-price-max');
    if (minSlider && maxSlider) {
      this.activeFilters.priceRange = { min: parseInt(minSlider.value), max: parseInt(maxSlider.value) };
    }
    this.applyFiltersAndDisplay();
  }
  updateActiveFilters() {
    this.activeFilters.gender = [];
    this.activeFilters.color = [];
    this.activeFilters.technicalDetails = [];
    
    this.searchFilters.querySelectorAll('.izas-filter-options[data-filter-type="gender"] .active').forEach(btn => {
      this.activeFilters.gender.push(btn.dataset.value);
    });
    
    this.searchFilters.querySelectorAll('.izas-color-swatches .active').forEach(btn => {
      this.activeFilters.color.push(btn.dataset.value);
    });
    this.searchFilters.querySelectorAll('#izas-dynamic-details input[type="checkbox"]:checked').forEach(cb => {
      this.activeFilters.technicalDetails.push(cb.value);
    });
  }
  handleInput(value) {
    clearTimeout(this.debounceTimer);
    this.currentQuery = value.trim();
    this.clearButton.style.display = this.currentQuery ? 'flex' : 'none';
    if (!this.currentQuery) {
      this.searchFilters.style.display = 'none';
      this.showDefaultResults();
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this.performSearch(this.currentQuery);
    }, 200);
  }
  async performSearch(query) {
    if (window.innerWidth > 768) {
      this.searchFilters.style.display = 'flex';
      if (this.mobileFilterToggle) this.mobileFilterToggle.style.display = 'none';
    } else {
      if (this.mobileFilterToggle) this.mobileFilterToggle.style.display = 'flex';
    }

    this.searchResults.innerHTML = `
      <div class="izas-search-loading">
        <div class="izas-loading-spinner"></div>
        <p>Buscando resultados...</p>
      </div>
    `;

    // Invalidamos cualquier búsqueda/paginación anterior en curso
    this.searchRequestId++;
    const requestId = this.searchRequestId;
    if (this.scrollObserver) this.scrollObserver.disconnect();
    this.searchPagination = {};
    this.allFetchedProducts = [];
    this.allFetchedProductIds = new Set();

    const correctedQuery = this.correctTypos(query);
    const synonyms = this.expandWithSynonyms(correctedQuery);

    try {
      // Mismos términos que antes: la palabra exacta, la corregida (si difiere) y hasta 2 sinónimos
      const searchTerms = [query];
      if (correctedQuery !== query) searchTerms.push(correctedQuery);
      const mainSynonyms = synonyms.filter(s => s !== correctedQuery && s !== query).slice(0, 2);
      searchTerms.push(...mainSynonyms);

      const searchPromises = searchTerms.map(term =>
        fetch(`/search?q=${encodeURIComponent(term)}*&type=product&view=ajax&page=1`)
          .then(r => r.json())
          .then(data => ({ term, data }))
          .catch(() => ({ term, data: [] }))
      );

      const collectionsPromise = fetch(`/search/suggest.json?q=${encodeURIComponent(correctedQuery)}&resources[type]=collection&resources[limit]=4`).then(r => r.json());

      const [results, collectionsData] = await Promise.all([Promise.all(searchPromises), collectionsPromise]);

      // Si el usuario ya ha lanzado otra búsqueda mientras esto volvía, la ignoramos
      if (requestId !== this.searchRequestId) return;

      results.forEach(({ term, data }) => {
        const rawProducts = Array.isArray(data) ? data : [];
        const relevantProducts = this.filterByTermRelevance(rawProducts, term);

        relevantProducts.forEach(product => {
          if (!this.allFetchedProductIds.has(product.id)) {
            this.allFetchedProductIds.add(product.id);
            this.allFetchedProducts.push(product);
          }
        });

        // El "hasMore" depende de si Shopify devolvió algo en esta página,
        // no de cuántos pasaron el filtro de relevancia (así no cortamos
        // la paginación solo porque la página 1 traiga ruido).
        this.searchPagination[term] = {
          page: 1,
          hasMore: rawProducts.length > 0,
          consecutiveEmpty: relevantProducts.length > 0 ? 0 : 1
        };
      });

      const collections = collectionsData.resources?.results?.collections || [];

      this.learnKeywordsFromResults(this.allFetchedProducts);
      this.initDynamicPrice(this.allFetchedProducts);
      this.populateDynamicFilters(this.allFetchedProducts);

      // Se persisten para que sigan disponibles al filtrar o hacer scroll
      this.savedCollections = collections;
      this.savedCorrectedQuery = correctedQuery !== query ? correctedQuery : null;

      this.applyFiltersAndDisplay(this.savedCollections, this.savedCorrectedQuery);

    } catch (error) {
      console.error('Error en búsqueda masiva:', error);
      this.displayResults([], [], null);
    }
  }
  /**
   * ¿Queda algún término de búsqueda con más páginas por pedir?
   */
  hasAnyMorePages() {
    return Object.values(this.searchPagination).some(state => state.hasMore);
  }
  /**
   * Pide la siguiente página de cada término que todavía tenga más
   * resultados, los añade a allFetchedProducts sin duplicar, y repinta
   * conservando la posición de scroll (para que no dé el salto al principio).
   */
  async loadMoreResults() {
    if (this.isLoadingMorePage) return;

    const MAX_SEARCH_PAGE = 40; // límite de seguridad anti-bucle-infinito
    const termsWithMore = Object.keys(this.searchPagination)
      .filter(t => this.searchPagination[t].hasMore && this.searchPagination[t].page < MAX_SEARCH_PAGE);

    if (termsWithMore.length === 0) return;

    this.isLoadingMorePage = true;
    const requestId = this.searchRequestId;

    try {
      const fetches = termsWithMore.map(term => {
        const nextPage = this.searchPagination[term].page + 1;
        return fetch(`/search?q=${encodeURIComponent(term)}*&type=product&view=ajax&page=${nextPage}`)
          .then(r => r.json())
          .then(data => ({ term, page: nextPage, data }))
          .catch(() => ({ term, page: nextPage, data: [] }));
      });

      const results = await Promise.all(fetches);
      if (requestId !== this.searchRequestId) return; // se lanzó otra búsqueda mientras tanto

      const CONSECUTIVE_EMPTY_LIMIT = 2; // 2 páginas seguidas sin nada relevante = dejamos de pedir ese término
      let addedAny = false;

      results.forEach(({ term, page, data }) => {
        const rawProducts = Array.isArray(data) ? data : [];
        const state = this.searchPagination[term];
        state.page = page;

        if (rawProducts.length === 0) {
          // Shopify ya no tiene más páginas para este término
          state.hasMore = false;
          return;
        }

        const relevantProducts = this.filterByTermRelevance(rawProducts, term);
        let addedForTerm = 0;
        relevantProducts.forEach(product => {
          if (!this.allFetchedProductIds.has(product.id)) {
            this.allFetchedProductIds.add(product.id);
            this.allFetchedProducts.push(product);
            addedForTerm++;
          }
        });

        if (addedForTerm > 0) {
          state.consecutiveEmpty = 0;
        } else {
          state.consecutiveEmpty = (state.consecutiveEmpty || 0) + 1;
          // La relevancia de Shopify ya se ha desviado del todo: dejamos de
          // pedir más páginas para este término
          if (state.consecutiveEmpty >= CONSECUTIVE_EMPTY_LIMIT) {
            state.hasMore = false;
          }
        }
        addedAny = addedAny || addedForTerm > 0;
      });

      if (addedAny) {
        this.learnKeywordsFromResults(this.allFetchedProducts);
        this.extendDynamicPriceRange(this.allFetchedProducts);
        this.populateDynamicFilters(this.allFetchedProducts);

        const previousScroll = this.searchResults.scrollTop;
        this.applyFiltersAndDisplay();
        this.searchResults.scrollTop = previousScroll;
      } else if (!this.hasAnyMorePages()) {
        // Ya no queda nada más en ningún término: quitamos el sentinel a mano
        if (this.scrollObserver) this.scrollObserver.disconnect();
        const sentinel = this.searchResults.querySelector('#izas-load-more-sentinel');
        if (sentinel) sentinel.remove();
      }
    } finally {
      this.isLoadingMorePage = false;
    }
  }
  /**
   * Observa el sentinel del final de la lista de productos y dispara
   * loadMoreResults() cuando entra en viewport (con un margen de 400px para
   * que empiece a cargar un poco antes de que el usuario llegue al final).
   */
  observeLoadMoreSentinel() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    const sentinel = this.searchResults.querySelector('#izas-load-more-sentinel');
    if (!sentinel) return;

    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) this.loadMoreResults();
      });
    }, {
      root: this.searchResults,
      rootMargin: '400px'
    });

    this.scrollObserver.observe(sentinel);
  }
  /**
   * Aprendizaje incremental del diccionario de nombres de producto.
   * Cada vez que una búsqueda trae resultados, extraemos sus palabras
   * "raras" (nombres de modelo) y las sumamos al diccionario en memoria
   * y en caché, por si el lote inicial (loadProductKeywords) no las cubrió.
   */
  learnKeywordsFromResults(products) {
    if (!products || products.length === 0) return;
    const newWords = this.buildProductKeywordsFromTitles(products.map(p => p.title));
    const before = this.productKeywords.length;
    this.productKeywords = [...new Set([...(this.productKeywords || []), ...newWords])];
    if (this.productKeywords.length !== before) {
      try {
        localStorage.setItem('izas_product_keywords', JSON.stringify({
          timestamp: Date.now(),
          keywords: this.productKeywords
        }));
      } catch (e) {
        // localStorage no disponible, no es crítico
      }
    }
  }
  applyFiltersAndDisplay(collections = null, correctedQuery = null) {
    if (collections === null) collections = this.savedCollections;
    if (correctedQuery === null) correctedQuery = this.savedCorrectedQuery;
    
    let filteredProducts = this.allFetchedProducts;
    // 1. GÉNERO
    if (this.activeFilters.gender.length > 0) {
      filteredProducts = filteredProducts.filter(p => {
        const titleLower = p.title.toLowerCase();
        const handleLower = p.handle.toLowerCase();
        const textToSearch = ` ${titleLower} ${handleLower} `;
        
        const metaGender = p.gender_meta ? JSON.stringify(p.gender_meta).toLowerCase() : '';
        
        let isProductUnisex = false;
        let isProductHombre = false;
        let isProductMujer = false;
        if (metaGender && metaGender !== '""' && metaGender !== 'null' && metaGender !== '[]' && metaGender !== '{}') {
          isProductUnisex = metaGender.includes('unisex');
          isProductHombre = metaGender.includes('hombre') || isProductUnisex;
          isProductMujer = metaGender.includes('mujer') || isProductUnisex;
        } else {
          const hasMaleIndicator = /\b(hombre|man|men|masculino|male)\b/.test(textToSearch) || /\b(m)\b/.test(titleLower);
          const hasFemaleIndicator = /\b(mujer|woman|women|femenino|female|lady)\b/.test(textToSearch) || /\b(w)\b/.test(titleLower);
          const hasUnisexWord = /\b(unisex)\b/.test(textToSearch);
          
          isProductUnisex = hasUnisexWord || (!hasMaleIndicator && !hasFemaleIndicator);
          isProductHombre = hasMaleIndicator || isProductUnisex;
          isProductMujer = hasFemaleIndicator || isProductUnisex;
        }
        return this.activeFilters.gender.some(genderFilter => {
          if (genderFilter === 'hombre') return isProductHombre;
          if (genderFilter === 'mujer') return isProductMujer;
          if (genderFilter === 'unisex') return isProductUnisex;
          return false;
        });
      });
    }
    // 2. COLOR (Con Ordenamiento de Relevancia: Predominante primero)
    if (this.activeFilters.color.length > 0) {
      filteredProducts = filteredProducts.filter(p => {
        let productDataString = (p.title + ' ' + p.handle).toLowerCase();
        if (p.tags && p.tags.length > 0) productDataString += ' ' + p.tags.join(' ').toLowerCase();
        
        let isPrimaryColor = false; // Variable para darle "nota" al producto
        if (p.variants && p.variants.length > 0) {
            p.variants.forEach(v => {
               if(v.title) {
                 const vTitle = v.title.toLowerCase();
                 productDataString += ' ' + vTitle;
                 
                 // Comprobar si el color es predominante (está ANTES de la barra '/')
                 this.activeFilters.color.forEach(activeColor => {
                    for (const key in this.COLOR_CONCEPTS) {
                      if (this.COLOR_CONCEPTS[key].canonical === activeColor) {
                        const firstPart = vTitle.split('/')[0].trim();
                        // Ver si la primera parte es un color primario exacto de nuestro diccionario
                        const matchesPrimary = this.COLOR_CONCEPTS[key].matches.some(match => firstPart === match.toLowerCase() || firstPart.includes(match.toLowerCase()));
                        // Ver si la variante entera tiene el color
                        const variantHasColor = this.COLOR_CONCEPTS[key].matches.some(match => vTitle.includes(match.toLowerCase()));
                        
                        if (variantHasColor && matchesPrimary) {
                          isPrimaryColor = true;
                        }
                      }
                    }
                 });
               }
            });
        }
        // Si no tiene variantes de color (ej: "S, M, L"), pero el TÍTULO del producto tiene el color, también es primario
        if (!isPrimaryColor) {
           const titleLower = p.title.toLowerCase();
           this.activeFilters.color.forEach(activeColor => {
              for (const key in this.COLOR_CONCEPTS) {
                 if (this.COLOR_CONCEPTS[key].canonical === activeColor) {
                    if (this.COLOR_CONCEPTS[key].matches.some(match => titleLower.includes(match.toLowerCase()))) {
                       isPrimaryColor = true;
                    }
                 }
              }
           });
        }
        const isMatch = this.activeFilters.color.some(activeColor => {
          for (const key in this.COLOR_CONCEPTS) {
            if (this.COLOR_CONCEPTS[key].canonical === activeColor) {
              return this.COLOR_CONCEPTS[key].matches.some(match => productDataString.includes(match.toLowerCase()));
            }
          }
          return false;
        });
        if (isMatch) {
          // Asignamos la puntuación: 1 (oro) si es predominante, 2 (plata) si es secundario
          p._colorSortScore = isPrimaryColor ? 1 : 2;
          return true;
        }
        return false;
      });
    }
    // 3. PRECIO
    if (this.activeFilters.priceRange) {
      filteredProducts = filteredProducts.filter(p => {
        const price = parseFloat(p.price || p.variants?.[0]?.price || 0);
        return price >= this.activeFilters.priceRange.min && price <= this.activeFilters.priceRange.max;
      });
    }
    // 4. DETALLES TÉCNICOS
    if (this.activeFilters.technicalDetails.length > 0) {
      filteredProducts = filteredProducts.filter(p => {
        if (!p.technical_details_meta) return false;
        const details = typeof p.technical_details_meta === 'string' 
          ? p.technical_details_meta.split(',') 
          : Array.isArray(p.technical_details_meta) ? p.technical_details_meta : [p.technical_details_meta];
        const normalizedDetails = details.map(d => typeof d === 'string' ? (d.trim().charAt(0).toUpperCase() + d.trim().slice(1)) : '');
        return this.activeFilters.technicalDetails.some(selectedDetail => normalizedDetails.includes(selectedDetail));
      });
    }
    // === APLICAR ORDENACIÓN FINAL ===
    const checkedSort = this.searchFilters.querySelector('input[name="izas-sort"]:checked');
    const sortValue = checkedSort ? checkedSort.value : 'relevance';
    
    // Guardamos la palabra exacta para darle prioridad VIP
    // Usamos split y cogemos la primera palabra para evitar fallos si escriben "chaqueta montana"
    const exactQueryLower = this.currentQuery.toLowerCase().split(' ')[0];
    filteredProducts.sort((a, b) => {
      // 1. RELEVANCIA (Orden por defecto)
      if (sortValue === 'relevance') {
        const titleALower = a.title.toLowerCase();
        const titleBLower = b.title.toLowerCase();
        // A. PRIORIDAD ABSOLUTA VIP: ¿La palabra original exacta está en el TÍTULO de forma exacta?
        // Usamos una Expresión Regular para buscar la palabra completa (ej: "montana" y no "montanar")
        const regexExact = new RegExp(`\\b${exactQueryLower}\\b`, 'i');
        const aHasExactWord = regexExact.test(titleALower) ? 1 : 0;
        const bHasExactWord = regexExact.test(titleBLower) ? 1 : 0;
        
        // Si uno tiene la palabra exacta y el otro no, ¡el que la tiene gana!
        if (aHasExactWord !== bHasExactWord) {
          return bHasExactWord - aHasExactWord; 
        }
        // B. PRIORIDAD SECUNDARIA: Truco del Color Predominante
        const scoreA = a._colorSortScore || 99; // Si no tiene score, le damos 99 (al fondo)
        const scoreB = b._colorSortScore || 99;
        if (scoreA !== scoreB) {
          return scoreA - scoreB;
        }
        // C. EMPATE: Dejar el orden como Shopify mande
        return 0;
      }
      // 2. OTROS ORDENAMIENTOS (Seleccionados por el usuario)
      if (sortValue === 'price-asc') {
        const priceA = parseFloat(a.price || a.variants?.[0]?.price || 0);
        const priceB = parseFloat(b.price || b.variants?.[0]?.price || 0);
        return priceA - priceB;
      }
      
      if (sortValue === 'price-desc') {
        const priceA = parseFloat(a.price || a.variants?.[0]?.price || 0);
        const priceB = parseFloat(b.price || b.variants?.[0]?.price || 0);
        return priceB - priceA;
      }
      
      if (sortValue === 'title-asc') return a.title.localeCompare(b.title);
      
      if (sortValue === 'title-desc') return b.title.localeCompare(a.title);
      
      if (sortValue === 'newest') return b.id - a.id;
      return 0;
    });
    this.displayResults(filteredProducts, collections, correctedQuery);
  }
  displayResults(products, collections, correctedQuery = null) {
    let html = '';
    if (correctedQuery) {
      html += `
        <div class="izas-search-suggestion">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1v14M1 8h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          <span>Mostrando resultados para: <strong>${correctedQuery}</strong></span>
        </div>
      `;
    }
    if (collections && collections.length > 0) {
      html += '<div class="izas-search-section">';
      html += '<h3 class="izas-search-section-title">Colecciones sugeridas</h3>';
      html += '<div class="izas-search-collections-suggested">';
      collections.forEach(collection => {
        html += `
          <a href="/collections/${collection.handle}" class="izas-search-collection-item-suggested">
            <span>${collection.title}</span>
          </a>
        `;
      });
      html += '</div></div>';
    }
    if (products.length > 0) {
      html += '<div class="izas-search-section">';
      html += `<h3 class="izas-search-section-title">Productos destacados</h3>`;
      html += '<div class="izas-search-products">';
      
      products.forEach(product => {
        let image = product.image || product.featured_image || product.images?.[0]?.src || '';
        let priceNum = parseFloat(product.price || product.variants?.[0]?.price || 0);
        let comparePriceNum = parseFloat(product.compare_at_price || 0);
        // Shopify calcula product.price y product.compare_at_price de forma
        // independiente (el precio más bajo entre variantes, y el precio
        // comparado de ESA misma variante más barata). Si esa variante no
        // está en oferta pero otra sí, product.compare_at_price llega vacío
        // aunque el producto SÍ tenga descuento real. Buscamos entre las
        // variantes una que sí lo tenga y mostramos su pareja precio/comparado.
        if (!(comparePriceNum > priceNum) && Array.isArray(product.variants)) {
          for (const v of product.variants) {
            const vPrice = parseFloat(v.price || 0);
            const vCompare = parseFloat(v.compare_at_price || 0);
            if (vPrice > 0 && vCompare > vPrice) {
              priceNum = vPrice;
              comparePriceNum = vCompare;
              break;
            }
          }
        }
        const price = this.formatPrice(priceNum);
        const comparePrice = comparePriceNum > priceNum ? this.formatPrice(comparePriceNum) : null;
        const available = product.available !== false;
        // === MAGIA DE CAMBIO DE IMAGEN POR COLOR (CON PRIORIDAD PREDOMINANTE) ===
        if (this.activeFilters.color && this.activeFilters.color.length > 0) {
          const activeColorCanonical = this.activeFilters.color[0]; // Cogemos el primer color marcado
          let colorMatches = [];
          
          // Sacamos las palabras clave de ese color del diccionario
          for (const key in this.COLOR_CONCEPTS) {
            if (this.COLOR_CONCEPTS[key].canonical === activeColorCanonical) {
              colorMatches = this.COLOR_CONCEPTS[key].matches;
              break;
            }
          }
          // Si el producto tiene variantes, buscamos la mejor foto posible
          if (product.variants && product.variants.length > 0) {
            let primaryVariant = null;   // Variante de Oro (Color predominante)
            let secondaryVariant = null; // Variante de Plata (Color secundario)
            for (const v of product.variants) {
              if (!v.title || !v.image) continue; // Saltamos si no tiene título o foto
              
              const variantTitleLower = v.title.toLowerCase();
              const firstPart = variantTitleLower.split('/')[0].trim(); // Lo que hay antes de la barra '/'
              
              // 1. Comprobamos si es el color principal
              const isPrimary = colorMatches.some(match => firstPart === match.toLowerCase() || firstPart.includes(match.toLowerCase()));
              
              // 2. Comprobamos si simplemente contiene el color (secundario)
              const hasColor = colorMatches.some(match => variantTitleLower.includes(match.toLowerCase()));
              if (hasColor) {
                if (isPrimary && !primaryVariant) {
                  primaryVariant = v; // ¡Encontramos la variante perfecta!
                  break; // Dejamos de buscar, ya tenemos la de Oro
                } else if (!secondaryVariant) {
                  secondaryVariant = v; // La guardamos por si acaso no hay ninguna principal
                }
              }
            }
            // Elegimos la ganadora: La principal si existe, si no, la secundaria
            const finalMatchedVariant = primaryVariant || secondaryVariant;
            
            if (finalMatchedVariant && finalMatchedVariant.image) {
              image = finalMatchedVariant.image; // ¡Hacemos el cambiazo de foto!
            }
          }
        }
        html += `
          <a href="${product.url || '/products/' + product.handle}" class="izas-search-product-item">
            <div class="izas-search-product-image">
              ${image ? `<img src="${image}" alt="${product.title}" loading="lazy">` : ''}
              ${!available ? '<span class="izas-product-badge">Agotado</span>' : ''}
            </div>
            <div class="izas-search-product-info">
              <h4>${product.title}</h4>
              <div class="izas-product-price-wrapper">
                <span class="izas-product-price">${price}</span>
                ${comparePrice ? `<span class="izas-product-compare-price">${comparePrice}</span>` : ''}
              </div>
            </div>
          </a>
        `;
      });
      
      html += '</div>';

      // Sentinel para el infinite scroll
      if (this.hasAnyMorePages()) {
        html += `
          <div id="izas-load-more-sentinel" class="izas-load-more-sentinel">
            <div class="izas-loading-spinner izas-loading-spinner-small"></div>
          </div>
        `;
      }

      html += '</div>';
    }
    // === VISTA DE CERO RESULTADOS (Con enlaces de ayuda) ===
    if (products.length === 0 && (!collections || collections.length === 0)) {
      html = `
        <div class="izas-search-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="30" stroke="currentColor" stroke-width="2" opacity="0.2"/><path d="M32 20v16M32 44h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          <h3>No se encontraron resultados</h3>
          <p>Intenta modificar tu búsqueda o limpiar los filtros.</p>
          <div class="izas-empty-links-section">
            <h4>Enlaces de interés</h4>
            <div class="izas-empty-links-grid">
              
              <a href="/policies/refund-policy" class="izas-empty-link-card">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
                <span>Cambios y Devoluciones</span>
              </a>
              <a href="/pages/localizador-de-tiendas" class="izas-empty-link-card">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                <span>Nuestras Tiendas</span>
              </a>
              <a href="/pages/guia-de-tallas-nueva" class="izas-empty-link-card">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="7" width="20" height="10" rx="2" ry="2"></rect>
                  <line x1="6" y1="7" x2="6" y2="11"></line>
                  <line x1="10" y1="7" x2="10" y2="14"></line>
                  <line x1="14" y1="7" x2="14" y2="11"></line>
                  <line x1="18" y1="7" x2="18" y2="14"></line>
                </svg>
                <span>Guía de Tallas</span>
              </a>
              <a href="/pages/preguntas-frecuentes" class="izas-empty-link-card">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                <span>Preguntas Frecuentes</span>
              </a>
              <a href="/pages/contact" class="izas-empty-link-card">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                <span>Contacto</span>
              </a>
              <a href="/policies/privacy-policy" class="izas-empty-link-card">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                <span>Política de Privacidad</span>
              </a>
            </div>
          </div>
        </div>
      `;
    }
    this.searchResults.innerHTML = html;
    this.observeLoadMoreSentinel();
  }
  showDefaultResults() {
    if (this.scrollObserver) this.scrollObserver.disconnect();
    this.searchPagination = {};
    this.searchFilters.style.display = 'none';
    this.mobileFilterToggle.style.display = 'none';
    this.searchFilters.classList.remove('izas-filters-open');
    
    const html = `
      <div class="izas-search-default">
        <h3 class="izas-search-section-title">Explorar Colecciones</h3>
        <div class="izas-default-carousel">
          <a href="/collections/nueva-coleccion" class="izas-carousel-item">
            <div class="izas-carousel-image">
              <img src="https://cdn.shopify.com/s/files/1/0834/1579/3985/files/Fondo_Rebajas_Movil.webp?v=1782297344" alt="Nueva Colección" loading="lazy">
            </div>
            <span class="izas-carousel-title">Nueva Colección</span>
          </a>
          
          <a href="/collections/hombre" class="izas-carousel-item">
            <div class="izas-carousel-image">
              <img src="https://cdn.shopify.com/s/files/1/0834/1579/3985/files/Chaquetas_Montana_Hombre.webp?v=1782891686" alt="Hombre" loading="lazy">
            </div>
            <span class="izas-carousel-title">Hombre</span>
          </a>
          <a href="/collections/mujer" class="izas-carousel-item">
            <div class="izas-carousel-image">
              <img src="https://cdn.shopify.com/s/files/1/0834/1579/3985/files/Mujer_Landing_Movil.webp?v=1782735983" alt="Mujer" loading="lazy">
            </div>
            <span class="izas-carousel-title">Mujer</span>
          </a>
          <a href="/collections/actividad" class="izas-carousel-item">
            <div class="izas-carousel-image">
              <img src="https://cdn.shopify.com/s/files/1/0834/1579/3985/files/Inspiracion_General_Home.webp?v=1782724004" alt="Outdoor" loading="lazy">
            </div>
            <span class="izas-carousel-title">Outdoor</span>
          </a>
        </div>

        <a href="/pages/fitness-week" class="izas-search-banner">
          <img src="https://cdn.shopify.com/s/files/1/0834/1579/3985/files/Fitness_Week_Banner_Escritorio.webp?v=1793686823" alt="Fitness Week" loading="lazy">
        </a>

        <div class="izas-empty-links-section">
          <h4>Enlaces de interés</h4>
          <div class="izas-empty-links-grid">
            
            <a href="/policies/refund-policy" class="izas-empty-link-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
              <span>Cambios y Devoluciones</span>
            </a>
            <a href="/pages/localizador-de-tiendas" class="izas-empty-link-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
              <span>Nuestras Tiendas</span>
            </a>
            <a href="/pages/guia-de-tallas-nueva" class="izas-empty-link-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="7" width="20" height="10" rx="2" ry="2"></rect>
                <line x1="6" y1="7" x2="6" y2="11"></line>
                <line x1="10" y1="7" x2="10" y2="14"></line>
                <line x1="14" y1="7" x2="14" y2="11"></line>
                <line x1="18" y1="7" x2="18" y2="14"></line>
              </svg>
              <span>Guía de Tallas</span>
            </a>
            <a href="/pages/preguntas-frecuentes" class="izas-empty-link-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
              <span>Preguntas Frecuentes</span>
            </a>
            <a href="/pages/contact" class="izas-empty-link-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
              <span>Contacto</span>
            </a>
            <a href="/policies/privacy-policy" class="izas-empty-link-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
              <span>Política de Privacidad</span>
            </a>
          </div>
        </div>
      </div>
    `;
    this.searchResults.innerHTML = html;
    requestAnimationFrame(() => this.syncBannerWidth());
  }
  formatPrice(price) {
    let numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(numPrice);
  }
  /**
   * Mide el ancho REAL en píxeles que ocupan las tarjetas del carrusel
   * (desde el borde izquierdo de la primera hasta el borde derecho de la última)
   * y se lo aplica al banner, para que empiecen y acaben exactamente en el mismo
   * punto sin importar el tamaño de pantalla, evitando desajustes por redondeo
   * de las unidades vw/clamp() usadas en el CSS.
   */
  syncBannerWidth() {
    const carousel = this.searchResults.querySelector('.izas-default-carousel');
    const banner = this.searchResults.querySelector('.izas-search-banner');
    if (!carousel || !banner) return;
    const items = carousel.querySelectorAll('.izas-carousel-item');
    if (items.length === 0) return;
    const firstRect = items[0].getBoundingClientRect();
    const lastRect = items[items.length - 1].getBoundingClientRect();
    const realWidth = lastRect.right - firstRect.left;
    if (realWidth > 0) {
      banner.style.width = realWidth + 'px';
      banner.style.maxWidth = '100%';
    }
  }
  disableNativeSearch() {
    const predictiveSearch = document.querySelector('predictive-search');
    if (predictiveSearch) {
      predictiveSearch.style.display = 'none';
      predictiveSearch.remove();
    }
  }
  preventHeaderOverlay() {
    const header = document.querySelector('store-header, .header, #storeHeader');
    if (header) header.classList.remove('search-is-active', 'search-open', 'search-is-expanded');
  }
  open() {
    this.isOpen = true;
    this.preventHeaderOverlay();
    this.searchOverlay.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    setTimeout(() => {
      this.searchOverlay.classList.add('izas-search-active');
      this.searchInput.focus();
    }, 10);
    if (!this.currentQuery) this.showDefaultResults();
  }
  close() {
    this.isOpen = false;
    this.searchOverlay.classList.remove('izas-search-active');
    
    setTimeout(() => {
      this.searchOverlay.style.display = 'none';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }, 300);
    this.searchInput.value = '';
    this.currentQuery = '';
    this.clearButton.style.display = 'none';
    this.searchFilters.style.display = 'none';

    if (this.scrollObserver) this.scrollObserver.disconnect();
    this.searchPagination = {};

    this.searchFilters.querySelectorAll('.izas-filter-btn, .izas-color-swatch-btn').forEach(btn => btn.classList.remove('active'));
    this.searchFilters.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false);
    this.activeFilters = { gender: [], color: [], priceRange: null, technicalDetails: [] };
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { window.izasSearch = new IzasSearch(); });
} else {
  window.izasSearch = new IzasSearch();
}